/**
 * lib/linienpruefung.js — 🆕 12.10.2026 · Auftrag LP1, R2/R3.
 *
 * Reine Funktionen für die Linienprüfung im Anker-Sammelmodus — ohne
 * React, damit `craco test` sie prüfen kann.
 *
 *   /admin/wizard/<id>?anker=1&t=<s>&liste=lp1&nr=<n>
 *
 * Nur die Listen in `LP_LISTEN` (`lp1`, 🆕 A45 `lp2`) schalten die Ebene ein; jede andere Liste geht wie bisher
 * an die Klickliste (`lib/klickliste.js`), ohne Liste bleibt der Wizard,
 * wie er ist.
 */

export const LP_LISTE = "lp1";
/** 🆕 Auftrag 45, R4: die Listen der Linienprüfung (Voreinstellung bleibt `lp1`). */
export const LP_LISTEN = ["lp1", "lp2", "fb1"];   // 🆕 44F FB2: fb1 (Strich-Liste)

export const URTEILE = [
  { wert: "richtig", text: "richtig" },
  { wert: "versetzt", text: "versetzt" },
  { wert: "fremdlinie", text: "Fremdlinie" },
  { wert: "keine_linie", text: "keine Linie" },
];

export const FREMD_ARTEN = [
  { wert: "fussball_torlinie", text: "Fußball-Torlinie" },
  { wert: "linie_5_5m", text: "5,5-m-Linie" },
  { wert: "linie_16_5m", text: "16,5-m-Linie" },
  { wert: "andere", text: "andere" },
];

export const ULTIMATE_LINIEN = [
  "back_line_left", "goal_line_left", "goal_line_right", "back_line_right",
  "sideline_bottom", "sideline_top",
];

/**
 * Welche Ebene der Anker-Modus aus den Sprung-Parametern bekommt.
 * `klickliste` ist exakt der bisherige Ausdruck aus `Wizard.jsx`, außer für
 * die Listen in `LP_LISTEN` — dann geht der Punkt an die Linienprüfung.
 */
export function ebenenAusSprung(sprung) {
  const mitListe = !!(sprung && sprung.anker && sprung.liste && sprung.nr);
  if (mitListe && LP_LISTEN.includes(sprung.liste)) {
    return { klickliste: null, linienpruefung: { liste: sprung.liste, nr: sprung.nr } };
  }
  return {
    klickliste: mitListe ? { name: sprung.liste, nr: sprung.nr } : null,
    linienpruefung: null,
  };
}

/** Von Listen-Pixeln (`bild` der Liste) in Bild-Pixel (`imgDims`) und zurück. */
export function zuBild(p, bild, imgDims) {
  const [bw, bh] = bild || [1280, 720];
  return [(p[0] * imgDims.w) / bw, (p[1] * imgDims.h) / bh];
}

export function zuListe(p, bild, imgDims) {
  const [bw, bh] = bild || [1280, 720];
  const x = Math.min(bw, Math.max(0, (p[0] * bw) / imgDims.w));
  const y = Math.min(bh, Math.max(0, (p[1] * bh) / imgDims.h));
  return [Math.round(x * 10) / 10, Math.round(y * 10) / 10];
}

/** Letztes Urteil je Linie eines Bildes: `{kennung: urteil}`; „fehlt“ getrennt. */
export function urteileVonBild(urteile, nr) {
  const linien = {};
  const fehlt = [];
  for (const u of urteile || []) {
    if (u.nr !== nr) continue;
    if (u.urteil === "fehlt") fehlt.push(u);
    else linien[u.linie_kennung] = u;
  }
  fehlt.sort((a, b) => a.linie_kennung.localeCompare(b.linie_kennung, "de", { numeric: true }));
  return { linien, fehlt };
}

/** Ein Bild ist fertig, wenn jede erkannte Linie ein Urteil hat (wie das Backend). */
export function bildFertig(punkt, urteile) {
  const { linien } = urteileVonBild(urteile, punkt.nr);
  return (punkt.linien || []).every((l) => !!linien[l.kennung]);
}

const nachNr = (punkte) => [...(punkte || [])].sort((a, b) => a.nr - b.nr);

export function punktZu(punkte, nr) {
  return (punkte || []).find((p) => p.nr === nr) || null;
}

export function vorheriger(punkte, nr) {
  const vor = nachNr(punkte).filter((p) => p.nr < nr);
  return vor.length ? vor[vor.length - 1] : null;
}

export function naechster(punkte, nr) {
  return nachNr(punkte).find((p) => p.nr > nr) || null;
}

/** Nächstes offenes Bild nach `nr`, am Ende von vorn; `null`, wenn alle fertig. */
export function naechsterOffener(punkte, urteile, nr) {
  const alle = nachNr(punkte);
  const offen = (p) => !bildFertig(p, urteile);
  const danach = alle.find((p) => p.nr > nr && offen(p));
  if (danach) return { punkt: danach, vonVorn: false };
  const vorn = alle.find((p) => p.nr < nr && offen(p));
  return vorn ? { punkt: vorn, vonVorn: true } : null;
}

export function fortschrittText(punkte, urteile, nr) {
  const n = (punkte || []).length;
  const fertig = (punkte || []).filter((p) => bildFertig(p, urteile)).length;
  return `Bild ${nr} von ${n} · ${fertig} fertig`;
}

/** Nächste freie Kennung „fehlt-<k>“ eines Bildes (1 … 12), sonst `null`. */
export function naechsteFehltKennung(fehlt) {
  const belegt = new Set((fehlt || []).map((u) => u.linie_kennung));
  for (let k = 1; k <= 12; k += 1) {
    if (!belegt.has(`fehlt-${k}`)) return `fehlt-${k}`;
  }
  return null;
}

/**
 * Der Körper für `PUT …/urteil`. Wirft bei fehlenden Angaben, damit die
 * Oberfläche nichts Unvollständiges schickt (das Backend prüft ohnehin).
 */
export function urteilKoerper({ kennung, urteil, art = null, klicks = null }) {
  if (![...URTEILE.map((u) => u.wert), "fehlt"].includes(urteil)) {
    throw new Error("Urteil unbekannt");
  }
  const body = { linie_kennung: kennung, urteil };
  if (urteil === "fremdlinie") {
    if (!FREMD_ARTEN.some((a) => a.wert === art)) throw new Error("Art der Fremdlinie fehlt");
    body.art = art;
  }
  if (urteil === "fehlt") {
    if (!ULTIMATE_LINIEN.includes(art)) throw new Error("Name der fehlenden Linie fehlt");
    body.art = art;
  }
  if (urteil === "versetzt" || urteil === "fehlt") {
    if (!klicks || klicks.length !== 2) throw new Error("Zwei Klicks nötig");
    body.korrektur = { p1: klicks[0], p2: klicks[1] };
  }
  return body;
}

/**
 * 🆕 LP1 Nachtrag 3, R7 (E-LP1-7): Die „Soll-Linien“ stammen aus Posen, die an den erkannten Linien gelöst sind
 * (41f R9b), nicht aus Handankern. Sie und alles, was an ihnen hängt („Phantom“, „x m vom Soll“), erscheinen nur bei
 * eingeschaltetem Schalter — Voreinstellung aus.
 */
export const SOLL_ZEIGEN_VOREINSTELLUNG = false;
export const SOLL_HINWEIS = "Pose an erkannten Linien gelöst";

/** Text hinter einer erkannten Linie in der Leiste, oder `null`. */
export function linienHinweis(linie, sollZeigen) {
  if (!sollZeigen || !linie || !linie.phantom) return null;
  const m = linie.abstand_soll_m != null ? ` · ${linie.abstand_soll_m} m vom Soll` : "";
  return `Phantom${m} (${SOLL_HINWEIS})`;
}

/**
 * 🆕 44F Stufe B, FB2 (E-44-20): Strich-Listen (`modus: "striche"`, Liste `fb1`). Es gibt keine erkannten Linien;
 * Alexander zieht jede sichtbare Linie dieser Arten mit zwei Klicks nach (Urteil „strich“, Kennung strich-1 … 30).
 * Nah/fern und links/rechts ergeben sich aus dem Bild. Arten wie `STRICH_ARTEN` im Backend.
 */
export const MODUS_STRICHE = "striche";
export const STRICH_MAX = 30;
export const STRICH_ARTEN = [
  { wert: "fussball_torlinie", text: "Fußball-Torlinie", richtung: "quer", farbe: "#f97316" },
  { wert: "torraumlinie", text: "Torraumlinie (5,5 m)", richtung: "quer", farbe: "#facc15" },
  { wert: "strafraumlinie", text: "Strafraumlinie (16,5 m)", richtung: "quer", farbe: "#a3e635" },
  { wert: "ultimate_grundlinie", text: "Ultimate-Grundlinie", richtung: "quer", farbe: "#38bdf8" },
  { wert: "ultimate_torlinie", text: "Ultimate-Torlinie", richtung: "quer", farbe: "#818cf8" },
  { wert: "torraum_seitenlinie", text: "Torraum-Seitenlinie", richtung: "längs", farbe: "#fde047" },
  { wert: "strafraum_seitenlinie", text: "Strafraum-Seitenlinie", richtung: "längs", farbe: "#bef264" },
  { wert: "ultimate_seitenlinie", text: "Ultimate-Seitenlinie", richtung: "längs", farbe: "#7dd3fc" },
];

export function istStrichListe(daten) {
  return !!daten && daten.modus === MODUS_STRICHE;
}

export function strichArt(wert) {
  return STRICH_ARTEN.find((a) => a.wert === wert) || null;
}

/** Striche eines Bildes, nach Kennung sortiert (strich-2 vor strich-10). */
export function stricheVonBild(urteile, nr) {
  return (urteile || []).filter((u) => u.nr === nr && u.urteil === "strich")
    .sort((a, b) => a.linie_kennung.localeCompare(b.linie_kennung, "de", { numeric: true }));
}

/** Nächste freie Kennung „strich-<k>“ eines Bildes (1 … 30), sonst `null`. */
export function naechsteStrichKennung(striche) {
  const belegt = new Set((striche || []).map((u) => u.linie_kennung));
  for (let k = 1; k <= STRICH_MAX; k += 1) {
    if (!belegt.has(`strich-${k}`)) return `strich-${k}`;
  }
  return null;
}

/** Der Körper für `PUT …/urteil` eines Strichs. Wirft bei unbekannter Art oder ohne zwei Klicks. */
export function strichKoerper({ kennung, art, klicks }) {
  if (!strichArt(art)) throw new Error("Art des Strichs unbekannt");
  if (!klicks || klicks.length !== 2) throw new Error("Zwei Klicks nötig");
  return { linie_kennung: kennung, urteil: "strich", art, korrektur: { p1: klicks[0], p2: klicks[1] } };
}

/** In einer Strich-Liste ist ein Bild fertig, wenn es mindestens einen Strich trägt (wie das Backend). */
export function strichBildFertig(punkt, urteile) {
  return stricheVonBild(urteile, punkt.nr).length > 0;
}

/** Nächstes Bild ohne Strich nach `nr`, am Ende von vorn; `null`, wenn alle einen Strich tragen. */
export function naechsterOhneStrich(punkte, urteile, nr) {
  const alle = nachNr(punkte);
  const offen = (p) => !strichBildFertig(p, urteile);
  const danach = alle.find((p) => p.nr > nr && offen(p));
  if (danach) return { punkt: danach, vonVorn: false };
  const vorn = alle.find((p) => p.nr < nr && offen(p));
  return vorn ? { punkt: vorn, vonVorn: true } : null;
}

export function strichFortschrittText(punkte, urteile, nr) {
  const n = (punkte || []).length;
  const fertig = (punkte || []).filter((p) => strichBildFertig(p, urteile)).length;
  return `Bild ${nr} von ${n} · ${fertig} mit Strichen`;
}

/** Dateiname des Exports. */
export function exportName(seqId, jetzt = new Date(), liste = LP_LISTE) {
  const p = (n) => String(n).padStart(2, "0");
  const stempel = `${jetzt.getFullYear()}-${p(jetzt.getMonth() + 1)}-${p(jetzt.getDate())}_`
    + `${p(jetzt.getHours())}${p(jetzt.getMinutes())}`;
  return `linienpruefung_${liste}_${String(seqId).slice(0, 8)}_${stempel}.json`;
}
